import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Checkbox, Modal } from 'semantic-ui-react'
import styled from 'styled-components'
import { Camera, ChartBar, Warning } from 'phosphor-react'
import { useAuth } from 'context'
import { useNotification } from 'hooks'
import {
    AppPrimaryButton,
    AppSecondaryButton,
    AppDangerButton,
} from 'elements'
import {
    DefaultText,
    PdfImageCompressionService,
    SettingsText,
} from 'utility'

const DEFAULTS = {
    Enabled: true,
    MaxDimensionPx: 1024,
    JpegQuality: 80,
    TargetKb: 0,
    PdfImageDisplayHeight: 80,
}

const DIMENSION_PRESETS = [
    {
        value: 512,
        label: 'Small',
        display: '512 px',
        hint: 'Smallest PDFs',
    },
    {
        value: 1024,
        label: 'Medium',
        display: '1024 px',
        hint: 'Recommended',
    },
    {
        value: 2048,
        label: 'Large',
        display: '2048 px',
        hint: '~ 4x file size',
    },
    {
        value: 0,
        label: 'No resize',
        display: 'Original',
        hint: 'Uploaded size',
        original: true,
    },
]

const QUALITY_PRESETS = [
    {
        value: 60,
        label: 'Low',
        display: '60',
        hint: 'Visible artifacts',
    },
    {
        value: 80,
        label: 'Medium',
        display: '80',
        hint: 'Recommended',
    },
    {
        value: 95,
        label: 'High',
        display: '95',
        hint: 'Near-original',
    },
    {
        value: 0,
        label: 'No re-encode',
        display: 'Original',
        hint: 'Raw bytes',
        original: true,
    },
]

const TARGET_KB_PRESETS = [
    { value: 0, display: 'Off', label: 'Use quality', hint: 'Default' },
    { value: 200, display: '~200 KB', label: 'Tight', hint: 'Email-safe' },
    {
        value: 500,
        display: '~500 KB',
        label: 'Balanced',
        hint: 'Facilco pick',
    },
    { value: 1000, display: '~1 MB', label: 'Detail', hint: 'Visual quality' },
    { value: 2000, display: '~2 MB', label: 'Rich', hint: 'Print-ready' },
]

const DISPLAY_HEIGHT_PRESETS = [
    {
        value: 80,
        display: '80 pt',
        label: 'Compact',
        hint: '~4 per page · default',
    },
    {
        value: 120,
        display: '120 pt',
        label: 'Standard',
        hint: '~3 per page',
    },
    {
        value: 180,
        display: '180 pt',
        label: 'Large',
        hint: '~2 per page',
    },
    {
        value: 240,
        display: '240 pt',
        label: 'Extra Large',
        hint: '~1.5 per page',
    },
]

// Rough model: estimated PDF MB for a 10-photo audit (dim_quality)
const ESTIMATE_MB = {
    '512_60': 0.8,
    '512_80': 1.0,
    '512_95': 1.4,
    '512_0': 8,
    '1024_60': 1.5,
    '1024_80': 2.5,
    '1024_95': 4.0,
    '1024_0': 15,
    '2048_60': 3.5,
    '2048_80': 6.0,
    '2048_95': 10,
    '2048_0': 25,
    '0_60': 12,
    '0_80': 20,
    '0_95': 30,
    '0_0': 40,
}

const getPresetBorder = ($selected, $original) => {
    if ($selected && $original) return '#f59e0b'
    if ($selected) return '#2563eb'
    if ($original) return '#fed7aa'
    return '#e5e7eb'
}

const getPresetBackground = ($selected, $original) => {
    if ($selected && $original) return '#ffedd5'
    if ($selected) return '#eef2ff'
    if ($original) return '#fff7ed'
    return '#ffffff'
}

const getPresetHoverBorder = ($selected, $original) => {
    if ($selected && $original) return '#f59e0b'
    if ($selected) return '#2563eb'
    return '#d1d5db'
}

const getPresetValueColor = ($selected, $original) => {
    if ($original) return '#c2410c'
    if ($selected) return '#2563eb'
    return '#111827'
}

const getPresetLabelColor = ($selected, $original) => {
    if ($original) return '#9a3412'
    if ($selected) return '#2563eb'
    return '#6b7280'
}

const getToneBackground = $tone => {
    if ($tone === 'danger') return '#fef2f2'
    if ($tone === 'warn') return '#fef3c7'
    return '#ecfdf5'
}

const getToneBorder = $tone => {
    if ($tone === 'danger') return '#fecaca'
    if ($tone === 'warn') return '#fcd34d'
    return '#a7f3d0'
}

const getToneStrongColor = $tone => {
    if ($tone === 'danger') return '#7f1d1d'
    if ($tone === 'warn') return '#78350f'
    return '#065f46'
}

const getToneSmallColor = $tone => {
    if ($tone === 'danger') return '#991b1b'
    if ($tone === 'warn') return '#78350f'
    return '#047857'
}

const getCompareBorder = ($highlight, $original) => {
    if (!$highlight) return '#e5e7eb'
    if ($original) return '#f59e0b'
    return '#2563eb'
}

const getCompareBackground = ($highlight, $original) => {
    if (!$highlight) return '#ffffff'
    if ($original) return '#fef3c7'
    return '#eef2ff'
}

const getCompareTextColor = ($highlight, $original, fallback) => {
    if (!$highlight) return fallback
    if ($original) return '#78350f'
    return '#2563eb'
}

const getStatusColor = $tone => {
    if ($tone === 'danger') return '#dc2626'
    if ($tone === 'warn') return '#b45309'
    if ($tone === 'ok') return '#16a34a'
    return '#6b7280'
}

const getEstimateIconColor = tone => {
    if (tone === 'danger') return '#dc2626'
    if (tone === 'warn') return '#b45309'
    return '#16a34a'
}

const getEstimateToneFromMb = mb => {
    if (mb >= 20) return 'danger'
    if (mb >= 8) return 'warn'
    return 'ok'
}

const getToggleHelpText = (enabled, isOriginalBypass) => {
    if (!enabled) {
        return 'Currently OFF — photos are embedded at their original size. A 10-photo audit with modern phone cameras (~4 MB each) will produce PDFs of ~40 MB or more.'
    }
    if (isOriginalBypass) {
        return 'ON, but with "Original" selected below → compression is effectively bypassed. Photos are embedded at their uploaded resolution and file size.'
    }
    return 'When on, photos are resized to the Max Dimension and re-encoded as JPEG at the chosen quality before embedding. When off, photos are embedded at their original size (may produce very large PDFs).'
}

const getQualityLabel = settings => {
    if (settings.TargetKb > 0) return `target ${settings.TargetKb} KB`
    if (settings.JpegQuality === 0) return 'Original'
    return `q=${settings.JpegQuality}`
}

const getFooterStatusTone = ({ enabled, dirty, isOriginalBypass }) => {
    if (!enabled) return 'danger'
    if (dirty) return 'warn'
    if (isOriginalBypass) return 'danger'
    return undefined
}

const getFooterStatusText = ({
    enabled,
    dirty,
    isOriginalBypass,
    savedStatus,
}) => {
    if (!enabled) return 'Compression: OFF'
    if (dirty) return 'Unsaved changes'
    if (isOriginalBypass) {
        return 'Original selected — large PDF sizes expected'
    }
    return savedStatus || 'Using current settings'
}

const getPreviewHelpText = (previewAvailable, previewLoading) => {
    if (previewLoading) {
        return 'Loading live preview…'
    }
    if (previewAvailable) {
        return 'Shows one sample photo with Low / Medium / High quality previews. Changing JPEG quality switches which preview image is shown. Max dimension and Target size affect real PDFs after Save — they are not fully simulated in this preview API. "Try another photo" asks the server for a different sample.'
    }
    return 'Live photo preview could not load from the server yet. The estimate above still updates from your selected presets.'
}

const getSamplePhotoLabel = (preview, previewLoading) => {
    if (preview?.SamplePhotoDescription) return preview.SamplePhotoDescription
    if (previewLoading) return 'Loading…'
    return 'Unavailable'
}

const Card = styled.div`
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
`

const CardHeader = styled.div`
    padding: 16px 20px;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    align-items: flex-start;
    gap: 12px;
`

const IconBg = styled.div`
    width: 40px;
    height: 40px;
    background: #eef2ff;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #2563eb;
    flex-shrink: 0;
`

const CardHeaderText = styled.div`
    flex: 1;
    h3 {
        margin: 0 0 4px 0;
        font-size: 16px;
    }
    p {
        margin: 0;
        color: #6b7280;
        font-size: 13px;
        line-height: 1.5;
    }
`

const CardBody = styled.div`
    padding: 20px;
`

const Row = styled.div`
    margin-bottom: 20px;
    &:last-child {
        margin-bottom: 0;
    }
`

const FieldLabel = styled.label`
    display: block;
    font-weight: 500;
    font-size: 14px;
    margin-bottom: 4px;
`

const FieldHelp = styled.p`
    font-size: 12px;
    color: #6b7280;
    margin: 0 0 10px 0;
    line-height: 1.5;
`

const NewBadge = styled.span`
    font-size: 11px;
    color: #f59e0b;
    text-transform: uppercase;
    margin-left: 6px;
`

const ToggleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px;
    background: #f9fafb;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
`

const ToggleText = styled.div`
    flex: 1;
    strong {
        display: block;
        font-size: 14px;
        margin-bottom: 2px;
    }
    small {
        color: ${({ $warn }) => ($warn ? '#b45309' : '#6b7280')};
        font-size: 12px;
        line-height: 1.5;
    }
`

const Presets = styled.div`
    display: grid;
    grid-template-columns: ${({ $cols }) =>
        $cols === 5 ? 'repeat(5, 1fr)' : 'repeat(4, 1fr)'};
    gap: 10px;
    opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
    pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
    @media (max-width: 900px) {
        grid-template-columns: repeat(2, 1fr);
    }
`

const Preset = styled.button`
    padding: 12px;
    border: 1.5px solid
        ${({ $selected, $original }) => getPresetBorder($selected, $original)};
    border-radius: 8px;
    cursor: pointer;
    background: ${({ $selected, $original }) =>
        getPresetBackground($selected, $original)};
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-family: inherit;
    transition: border-color 0.15s;
    &:hover {
        border-color: ${({ $selected, $original }) =>
            getPresetHoverBorder($selected, $original)};
    }
`

const PresetValue = styled.div`
    font-size: 15px;
    font-weight: 600;
    color: ${({ $selected, $original }) =>
        getPresetValueColor($selected, $original)};
`

const PresetLabel = styled.div`
    font-size: 13px;
    color: ${({ $selected, $original }) =>
        getPresetLabelColor($selected, $original)};
`

const PresetHint = styled.div`
    font-size: 11px;
    color: #6b7280;
    margin-top: 4px;
`

const Estimate = styled.div`
    background: ${({ $tone }) => getToneBackground($tone)};
    border: 1px solid ${({ $tone }) => getToneBorder($tone)};
    border-radius: 8px;
    padding: 16px;
    display: flex;
    align-items: center;
    gap: 16px;
    opacity: ${({ $dimmed }) => ($dimmed ? 0.5 : 1)};
`

const EstimateText = styled.div`
    flex: 1;
    strong {
        display: block;
        font-size: 15px;
        color: ${({ $tone }) => getToneStrongColor($tone)};
    }
    small {
        color: ${({ $tone }) => getToneSmallColor($tone)};
        font-size: 12px;
    }
`

const EstimateValue = styled.div`
    font-size: 20px;
    font-weight: 700;
    color: ${({ $tone }) => getToneStrongColor($tone)};
    white-space: nowrap;
`

const Compare = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-top: 12px;
    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`

const CompareItem = styled.div`
    border: 1px solid
        ${({ $highlight, $original }) =>
            getCompareBorder($highlight, $original)};
    border-radius: 8px;
    padding: 10px;
    text-align: center;
    background: ${({ $highlight, $original }) =>
        getCompareBackground($highlight, $original)};
`

const ImgBox = styled.div`
    background: #e5e7eb;
    height: 120px;
    border-radius: 4px;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #9ca3af;
    font-size: 11px;
    overflow: hidden;
    @media (max-width: 900px) {
        height: 160px;
    }
    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        background: #f3f4f6;
    }
`

const FName = styled.div`
    font-size: 12px;
    font-weight: 500;
    color: ${({ $highlight, $original }) =>
        getCompareTextColor($highlight, $original, '#111827')};
`

const FSize = styled.div`
    font-size: 11px;
    color: ${({ $highlight, $original }) =>
        getCompareTextColor($highlight, $original, '#6b7280')};
`

const PreviewMeta = styled.div`
    margin-top: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
    color: #6b7280;
    gap: 8px;
    flex-wrap: wrap;
`

const GhostButton = styled.button`
    background: transparent;
    color: #6b7280;
    border: 1px solid transparent;
    border-radius: 6px;
    padding: 4px 10px;
    font-size: 12px;
    cursor: pointer;
    font-family: inherit;
    &:hover {
        background: #f3f4f6;
        color: #111827;
    }
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`

const CardFooter = styled.div`
    padding: 14px 20px;
    border-top: 1px solid #e5e7eb;
    background: #fafafa;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    position: relative;
    z-index: 2;
    /* Keep actions clear of floating WhatsApp / sync widgets */
    padding-bottom: 20px;
    @media (max-width: 900px) {
        flex-direction: column;
        align-items: stretch;
        padding-bottom: 72px;
    }
`

const Status = styled.div`
    color: ${({ $tone }) => getStatusColor($tone)};
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
`

const Actions = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
    @media (max-width: 900px) {
        width: 100%;
        justify-content: stretch;
        button {
            flex: 1;
        }
    }
`

const ModalWarn = styled.div`
    background: #fef3c7;
    border-left: 3px solid #f59e0b;
    padding: 10px 12px;
    font-size: 12px;
    color: #78350f;
    margin: 14px 0;
    border-radius: 4px;
`

const ModalList = styled.ul`
    font-size: 13px;
    color: #111827;
    margin: 0 0 12px 20px;
    padding: 0;
    line-height: 1.7;
`

const normalizeSettings = data => ({
    Enabled: data?.Enabled ?? DEFAULTS.Enabled,
    MaxDimensionPx: data?.MaxDimensionPx ?? DEFAULTS.MaxDimensionPx,
    JpegQuality: data?.JpegQuality ?? DEFAULTS.JpegQuality,
    TargetKb: data?.TargetKb ?? DEFAULTS.TargetKb,
    PdfImageDisplayHeight:
        data?.PdfImageDisplayHeight ?? DEFAULTS.PdfImageDisplayHeight,
})

const settingsEqual = (a, b) =>
    !!a?.Enabled === !!b?.Enabled &&
    Number(a?.MaxDimensionPx) === Number(b?.MaxDimensionPx) &&
    Number(a?.JpegQuality) === Number(b?.JpegQuality) &&
    Number(a?.TargetKb) === Number(b?.TargetKb) &&
    Number(a?.PdfImageDisplayHeight) === Number(b?.PdfImageDisplayHeight)

const formatSavedAt = (iso, name) => {
    if (!iso) return null
    try {
        const d = new Date(iso)
        const stamp = d.toISOString().replace('T', ' ').slice(0, 16)
        return name
            ? `Last saved ${stamp} UTC by ${name}`
            : `Last saved ${stamp} UTC`
    } catch {
        return null
    }
}

const getEstimate = settings => {
    if (!settings.Enabled) {
        return {
            label: '~ 40+ MB',
            tone: 'warn',
            help: 'Compression is off. Actual size depends on original photo dimensions and encoding.',
        }
    }
    if (settings.TargetKb > 0) {
        const mb = ((settings.TargetKb * 10) / 1024).toFixed(1)
        return {
            label: `~ ${mb} MB`,
            tone: settings.TargetKb >= 1000 ? 'warn' : 'ok',
            help: `Target size ~${settings.TargetKb} KB per photo. JPEG quality preset is ignored.`,
        }
    }
    if (settings.MaxDimensionPx === 0 && settings.JpegQuality === 0) {
        return {
            label: '~ 40 MB',
            tone: 'danger',
            help: '"Original" bypasses all compression. Modern phone photos are 3-5 MB each; ten of them can push PDFs past email attachment limits (typically 25 MB).',
        }
    }
    const key = `${settings.MaxDimensionPx}_${settings.JpegQuality}`
    const mb = ESTIMATE_MB[key] ?? 2.5
    return {
        label: `~ ${mb} MB`,
        tone: getEstimateToneFromMb(mb),
        help: 'Rough model based on your current settings. Actual file size depends on photo content.',
    }
}

const PresetChip = ({ preset, selected, onSelect, disabled }) => (
    <Preset
        type="button"
        $selected={selected}
        $original={!!preset.original}
        disabled={disabled}
        onClick={() => onSelect(preset.value)}
    >
        <PresetValue $selected={selected} $original={!!preset.original}>
            {preset.display}
        </PresetValue>
        <PresetLabel $selected={selected} $original={!!preset.original}>
            {preset.label}
        </PresetLabel>
        {preset.hint && <PresetHint>{preset.hint}</PresetHint>}
    </Preset>
)

export const PdfImageCompressionSettings = () => {
    const { user } = useAuth()
    const { successToast, errorToast } = useNotification()
    const companyId = user?.CompanyId

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [resetting, setResetting] = useState(false)
    const [resetOpen, setResetOpen] = useState(false)
    const [settings, setSettings] = useState(DEFAULTS)
    const [saved, setSaved] = useState(DEFAULTS)
    const [meta, setMeta] = useState({
        LastModifiedOn: null,
        LastModifiedByName: null,
    })
    const [preview, setPreview] = useState(null)
    const [previewLoading, setPreviewLoading] = useState(false)
    const [previewAvailable, setPreviewAvailable] = useState(false)
    const previewRequestId = useRef(0)

    const dirty = useMemo(() => !settingsEqual(settings, saved), [settings, saved])
    const estimate = useMemo(() => getEstimate(settings), [settings])
    const isOriginalBypass =
        settings.Enabled &&
        settings.MaxDimensionPx === 0 &&
        settings.JpegQuality === 0 &&
        !settings.TargetKb
    const qualityIgnored = settings.Enabled && settings.TargetKb > 0

    const applyResponse = useCallback(data => {
        const next = normalizeSettings(data)
        setSettings(next)
        setSaved(next)
        setMeta({
            LastModifiedOn: data?.LastModifiedOn || null,
            LastModifiedByName: data?.LastModifiedByName || null,
        })
    }, [])

    // Load settings once per company (do not depend on toast fns — they are new each render)
    useEffect(() => {
        if (!companyId) return undefined
        let cancelled = false
        const fetchSettings = async () => {
            setLoading(true)
            try {
                const data = await PdfImageCompressionService.get(companyId)
                if (!cancelled) applyResponse(data)
            } catch {
                if (!cancelled) {
                    applyResponse(DEFAULTS)
                    errorToast(DefaultText.defaultError)
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        }
        fetchSettings()
        return () => {
            cancelled = true
        }
    }, [companyId, applyResponse])

    const loadPreview = useCallback(async (notify = false) => {
        if (!companyId) return
        const requestId = previewRequestId.current + 1
        previewRequestId.current = requestId
        const previousSample = preview?.SamplePhotoDescription
        setPreviewLoading(true)
        try {
            const data = await PdfImageCompressionService.preview(companyId)
            if (requestId !== previewRequestId.current) return
            setPreview(data)
            setPreviewAvailable(!!data?.Success || !!data?.Variants?.length)
            if (notify) {
                const nextSample = data?.SamplePhotoDescription
                if (nextSample && previousSample && nextSample === previousSample) {
                    successToast(
                        'Preview refreshed — server returned the same sample photo',
                    )
                } else if (nextSample) {
                    successToast(`Loaded sample: ${nextSample}`)
                } else {
                    successToast('Preview photo refreshed')
                }
            }
        } catch {
            if (requestId !== previewRequestId.current) return
            setPreview(null)
            setPreviewAvailable(false)
            if (notify) errorToast('Could not load photo preview')
        } finally {
            if (requestId === previewRequestId.current) {
                setPreviewLoading(false)
            }
        }
    }, [companyId, preview?.SamplePhotoDescription])

    // Preview API returns Low/Medium/High variants for one sample photo.
    // Fetch once on load (and via "Try another photo") — not on every preset click.
    useEffect(() => {
        if (!companyId) return undefined
        loadPreview(false)
        return undefined
    }, [companyId])

    const handleTryAnotherPhoto = () => {
        loadPreview(true)
    }

    const updateField = (key, value) => {
        setSettings(prev => ({ ...prev, [key]: value }))
    }

    const handleSave = async () => {
        if (!companyId || !dirty) return
        setSaving(true)
        const payload = {
            Enabled: !!settings.Enabled,
            MaxDimensionPx: Number(settings.MaxDimensionPx),
            JpegQuality: Number(settings.JpegQuality),
            TargetKb: Number(settings.TargetKb),
            PdfImageDisplayHeight: Number(settings.PdfImageDisplayHeight),
        }
        try {
            const data = await PdfImageCompressionService.update(
                companyId,
                payload,
            )
            if (data?.Success === false) {
                errorToast(data?.ErrorMessage || DefaultText.defaultError)
                return
            }
            // Prefer server fields; fall back to what we just saved so dirty clears
            applyResponse({
                ...payload,
                ...(data || {}),
                LastModifiedOn:
                    data?.LastModifiedOn || new Date().toISOString(),
                LastModifiedByName:
                    data?.LastModifiedByName ||
                    user?.UserName ||
                    user?.Email ||
                    'you',
            })
            successToast(SettingsText.pdfImageCompressionSaved)
        } catch (err) {
            const msg =
                err?.error?.ErrorMessage ||
                err?.error?.errorMessage ||
                DefaultText.defaultError
            errorToast(msg)
        } finally {
            setSaving(false)
        }
    }

    const handleResetConfirm = async () => {
        if (!companyId) return
        setResetting(true)
        try {
            const data = await PdfImageCompressionService.reset(companyId)
            applyResponse(data || DEFAULTS)
            setResetOpen(false)
            successToast(SettingsText.pdfImageCompressionReset)
        } catch {
            errorToast(DefaultText.defaultError)
        } finally {
            setResetting(false)
        }
    }

    const handleCancel = () => {
        setSettings(saved)
    }

    const matchingVariant = useMemo(() => {
        const variants = preview?.Variants || []
        if (!variants.length) return null
        if (!settings.Enabled) return null
        // Preview API only returns Low/Medium/High quality variants.
        // When TargetKb is set, show Medium as a rough visual stand-in.
        if (settings.TargetKb > 0) {
            return (
                variants.find(v => v.Preset === 'Medium') ||
                variants[1] ||
                variants[0]
            )
        }
        if (settings.JpegQuality === 0) return null
        const byQuality = variants.find(
            v => Number(v.Quality) === Number(settings.JpegQuality),
        )
        if (byQuality) return byQuality
        return (
            variants.find(v => v.Preset === 'Medium') ||
            variants[0]
        )
    }, [preview, settings])

    // API may not send SourceDataUrl — fall back to High variant as nearest original
    const sourceImageSrc = useMemo(() => {
        if (preview?.SourceDataUrl) return preview.SourceDataUrl
        if (preview?.SourceUrl) return preview.SourceUrl
        const variants = preview?.Variants || []
        const high =
            variants.find(v => v.Preset === 'High') ||
            variants[variants.length - 1]
        return high?.DataUrl || high?.Url || ''
    }, [preview])

    const currentImageSrc =
        matchingVariant?.DataUrl || matchingVariant?.Url || sourceImageSrc

    const sourceMetaLabel = useMemo(() => {
        if (preview?.SourceSizeKb || preview?.SourceWidth) {
            const size = preview.SourceSizeKb
                ? `${preview.SourceSizeKb} KB`
                : '—'
            const dims =
                preview.SourceWidth && preview.SourceHeight
                    ? `${preview.SourceWidth}×${preview.SourceHeight}`
                    : ''
            return dims ? `${size} · ${dims}` : size
        }
        return '3.4 MB · 4032×3024'
    }, [preview])

    const previewSummary = useMemo(() => {
        if (!settings.Enabled) {
            return {
                label: 'Compression off',
                size: 'Original size',
                diff: 'No compression applied',
                originalHighlight: false,
            }
        }
        if (isOriginalBypass) {
            return {
                label: 'Original / Original',
                size: 'Same as source',
                diff: 'Pixel-perfect, largest PDFs',
                originalHighlight: true,
            }
        }
        const dimLabel =
            settings.MaxDimensionPx === 0
                ? 'Original'
                : `${settings.MaxDimensionPx} px`
        const qLabel = getQualityLabel(settings)
        const sizeLabel = matchingVariant?.SizeKb
            ? `${matchingVariant.SizeKb} KB`
            : estimate.label.replace('~ ', '~')
        let diff = 'Minor detail loss on fine textures'
        if (settings.MaxDimensionPx === 512 && settings.JpegQuality === 60) {
            diff = 'Noticeable artifacts on details'
        } else if (settings.TargetKb > 0) {
            diff = 'Size driven by target KB (quality auto-tuned on server)'
        } else if (settings.JpegQuality === 95) {
            diff = 'Near-original detail'
        }
        return {
            label: `${dimLabel} / ${qLabel}`,
            size: sizeLabel,
            diff,
            originalHighlight: false,
        }
    }, [settings, isOriginalBypass, matchingVariant, estimate])

    const savedStatus = formatSavedAt(
        meta.LastModifiedOn,
        meta.LastModifiedByName,
    )

    if (loading) {
        return <div style={{ padding: 20, color: '#6b7280' }}>Loading…</div>
    }

    return (
        <>
            <Card>
                <CardHeader>
                    <IconBg>
                        <Camera size={20} weight="duotone" />
                    </IconBg>
                    <CardHeaderText>
                        <h3>PDF Image Compression</h3>
                        <p>
                            Controls how uploaded photos are compressed when
                            embedded in PDF reports for this company. Smaller
                            files download faster and email attach better, but
                            photos lose fine detail.{' '}
                            <strong>
                                Recommended: keep this on with the Medium
                                presets.
                            </strong>
                        </p>
                    </CardHeaderText>
                </CardHeader>

                <CardBody>
                    <Row>
                        <ToggleRow>
                            <Checkbox
                                toggle
                                checked={!!settings.Enabled}
                                onChange={(_, data) =>
                                    updateField('Enabled', !!data?.checked)
                                }
                            />
                            <ToggleText $warn={!settings.Enabled}>
                                <strong>Compress photos in PDFs</strong>
                                <small>
                                    {getToggleHelpText(
                                        settings.Enabled,
                                        isOriginalBypass,
                                    )}
                                </small>
                            </ToggleText>
                        </ToggleRow>
                    </Row>

                    <Row>
                        <FieldLabel>Max photo dimension (pixels)</FieldLabel>
                        <FieldHelp>
                            The longer edge of each photo is scaled down to this
                            many pixels. Photos smaller than this are embedded
                            as-is.{' '}
                            <strong>&quot;Original&quot;</strong> keeps the
                            photo at its uploaded resolution (no resize).
                        </FieldHelp>
                        <Presets $disabled={!settings.Enabled}>
                            {DIMENSION_PRESETS.map(preset => (
                                <PresetChip
                                    key={preset.value}
                                    preset={preset}
                                    selected={
                                        Number(settings.MaxDimensionPx) ===
                                        preset.value
                                    }
                                    onSelect={v =>
                                        updateField('MaxDimensionPx', v)
                                    }
                                />
                            ))}
                        </Presets>
                    </Row>

                    <Row>
                        <FieldLabel>JPEG quality</FieldLabel>
                        <FieldHelp>
                            How much detail to preserve when re-encoding. Lower
                            quality = smaller files but more visible compression
                            noise.{' '}
                            <strong>&quot;Original&quot;</strong> skips
                            re-encoding entirely and embeds the uploaded bytes
                            as-is.{' '}
                            <em>Ignored when Target Size below is set.</em>
                        </FieldHelp>
                        <Presets
                            $disabled={!settings.Enabled || qualityIgnored}
                        >
                            {QUALITY_PRESETS.map(preset => (
                                <PresetChip
                                    key={preset.value}
                                    preset={preset}
                                    selected={
                                        Number(settings.JpegQuality) ===
                                        preset.value
                                    }
                                    onSelect={v =>
                                        updateField('JpegQuality', v)
                                    }
                                />
                            ))}
                        </Presets>
                    </Row>

                    <Row>
                        <FieldLabel>
                            Target size per photo <NewBadge>NEW</NewBadge>
                        </FieldLabel>
                        <FieldHelp>
                            When set, the server binary-searches JPEG quality
                            (30–92) to keep each photo close to this size. Max
                            Dimension is still honored, but{' '}
                            <strong>JPEG quality above is ignored</strong>. Leave
                            OFF for the classic dimension + quality flow.
                        </FieldHelp>
                        <Presets $cols={5} $disabled={!settings.Enabled}>
                            {TARGET_KB_PRESETS.map(preset => (
                                <PresetChip
                                    key={preset.value}
                                    preset={preset}
                                    selected={
                                        Number(settings.TargetKb) ===
                                        preset.value
                                    }
                                    onSelect={v => updateField('TargetKb', v)}
                                />
                            ))}
                        </Presets>
                    </Row>

                    <Row>
                        <FieldLabel>
                            Photo size on the page <NewBadge>NEW</NewBadge>
                        </FieldLabel>
                        <FieldHelp>
                            How tall the image cell renders in the PDF (in PDF
                            points). This is independent of compression, target
                            size, or Max Dimension — a customer can pick medium
                            file size + large display without touching the
                            compression toggle. Applies to every PDF generator
                            (standard Report, Franchise, Custom Report, and the
                            photo-summary grid).
                        </FieldHelp>
                        <Presets>
                            {DISPLAY_HEIGHT_PRESETS.map(preset => (
                                <PresetChip
                                    key={preset.value}
                                    preset={preset}
                                    selected={
                                        Number(
                                            settings.PdfImageDisplayHeight,
                                        ) === preset.value
                                    }
                                    onSelect={v =>
                                        updateField('PdfImageDisplayHeight', v)
                                    }
                                />
                            ))}
                        </Presets>
                    </Row>

                    <Row>
                        <Estimate
                            $tone={estimate.tone}
                            $dimmed={!settings.Enabled}
                        >
                            <div
                                style={{
                                    fontSize: 24,
                                    color: getEstimateIconColor(estimate.tone),
                                    display: 'flex',
                                }}
                            >
                                {estimate.tone === 'ok' ? (
                                    <ChartBar size={28} weight="duotone" />
                                ) : (
                                    <Warning size={28} weight="duotone" />
                                )}
                            </div>
                            <EstimateText $tone={estimate.tone}>
                                <strong>
                                    Estimated PDF size for a 10-photo audit
                                </strong>
                                <small>{estimate.help}</small>
                            </EstimateText>
                            <EstimateValue $tone={estimate.tone}>
                                {estimate.label}
                            </EstimateValue>
                        </Estimate>
                    </Row>

                    <Row>
                        <FieldLabel>Preview — how your photos will look</FieldLabel>
                        <FieldHelp>
                            {getPreviewHelpText(
                                previewAvailable,
                                previewLoading,
                            )}
                        </FieldHelp>
                        <Compare>
                            <CompareItem>
                                <ImgBox>
                                    {sourceImageSrc ? (
                                        <img
                                            src={sourceImageSrc}
                                            alt="Original upload"
                                        />
                                    ) : (
                                        'Original upload'
                                    )}
                                </ImgBox>
                                <FName>source.jpg</FName>
                                <FSize>{sourceMetaLabel}</FSize>
                            </CompareItem>
                            <CompareItem
                                $highlight
                                $original={previewSummary.originalHighlight}
                            >
                                <ImgBox>
                                    {currentImageSrc ? (
                                        <img
                                            src={currentImageSrc}
                                            alt="Your settings"
                                        />
                                    ) : (
                                        <>
                                            Your settings
                                            <br />
                                            <small style={{ opacity: 0.7 }}>
                                                {previewSummary.label}
                                            </small>
                                        </>
                                    )}
                                </ImgBox>
                                <FName
                                    $highlight
                                    $original={previewSummary.originalHighlight}
                                >
                                    preview_current.jpg
                                </FName>
                                <FSize
                                    $highlight
                                    $original={previewSummary.originalHighlight}
                                >
                                    {previewSummary.size}
                                    {!previewSummary.originalHighlight &&
                                        ' · what recipients see'}
                                </FSize>
                            </CompareItem>
                            <CompareItem>
                                <ImgBox>Difference</ImgBox>
                                <FName>
                                    {previewSummary.originalHighlight
                                        ? 'Same file'
                                        : 'File size change'}
                                </FName>
                                <FSize>{previewSummary.diff}</FSize>
                            </CompareItem>
                        </Compare>
                        <PreviewMeta>
                            <span>
                                Sample photo:{' '}
                                <em>
                                    {getSamplePhotoLabel(
                                        preview,
                                        previewLoading,
                                    )}
                                </em>
                            </span>
                            <GhostButton
                                type="button"
                                onClick={handleTryAnotherPhoto}
                                disabled={previewLoading}
                            >
                                {previewLoading
                                    ? 'Loading photo…'
                                    : 'Try another photo'}
                            </GhostButton>
                        </PreviewMeta>
                    </Row>
                </CardBody>

                <CardFooter>
                    <Status
                        $tone={getFooterStatusTone({
                            enabled: settings.Enabled,
                            dirty,
                            isOriginalBypass,
                        })}
                    >
                        {getFooterStatusText({
                            enabled: settings.Enabled,
                            dirty,
                            isOriginalBypass,
                            savedStatus,
                        })}
                    </Status>
                    <Actions>
                        {dirty ? (
                            <AppSecondaryButton
                                type="button"
                                onClick={handleCancel}
                                disabled={saving}
                            >
                                Cancel
                            </AppSecondaryButton>
                        ) : (
                            <GhostButton
                                type="button"
                                onClick={() => setResetOpen(true)}
                                disabled={saving || resetting}
                            >
                                Reset to defaults
                            </GhostButton>
                        )}
                        <AppPrimaryButton
                            type="button"
                            onClick={handleSave}
                            loading={saving}
                            disabled={!dirty || saving}
                        >
                            Save changes
                        </AppPrimaryButton>
                    </Actions>
                </CardFooter>
            </Card>

            <Modal
                open={resetOpen}
                onClose={() => !resetting && setResetOpen(false)}
                size="tiny"
            >
                <Modal.Header>Reset to defaults?</Modal.Header>
                <Modal.Content>
                    <p style={{ color: '#6b7280', fontSize: 13, marginTop: 0 }}>
                        This restores the recommended platform defaults for this
                        company:
                    </p>
                    <ModalList>
                        <li>
                            Compression: <strong>Enabled</strong>
                        </li>
                        <li>
                            Max dimension: <strong>1024 px (Medium)</strong>
                        </li>
                        <li>
                            JPEG quality: <strong>80 (Medium)</strong>
                        </li>
                        <li>
                            Target size: <strong>Off</strong>
                        </li>
                        <li>
                            Photo size on page:{' '}
                            <strong>80 pt (Compact)</strong>
                        </li>
                    </ModalList>
                    <ModalWarn>
                        <strong>Only future PDFs are affected.</strong> PDFs
                        already generated for completed audits keep their
                        existing rendering until they&apos;re re-completed.
                    </ModalWarn>
                </Modal.Content>
                <Modal.Actions>
                    <AppSecondaryButton
                        type="button"
                        onClick={() => setResetOpen(false)}
                        disabled={resetting}
                    >
                        Cancel
                    </AppSecondaryButton>
                    <AppDangerButton
                        type="button"
                        onClick={handleResetConfirm}
                        loading={resetting}
                        disabled={resetting}
                    >
                        Reset
                    </AppDangerButton>
                </Modal.Actions>
            </Modal>
        </>
    )
}
